import React, { createContext, useContext, useCallback, useMemo } from 'react';
import api from '../services/api';

const ApiContext = createContext();

export const useApi = () => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApi must be used within an ApiProvider');
  }
  return context;
};

export const ApiProvider = ({ children }) => {
  const apiCall = useCallback(async (methodOrUrl, urlOrOptions = {}, dataOrConfig = null) => {
    try {
      let method, url, config = {};
      
      // Detect old format: apiCall('METHOD', 'URL', data) or new format: apiCall('URL', { method: 'METHOD' })
      if (typeof urlOrOptions === 'string') {
        // Old format: apiCall('GET', '/api/endpoint', data)
        method = methodOrUrl;
        url = urlOrOptions;
        if (dataOrConfig) {
          config.data = dataOrConfig;
        }
      } else {
        // New format: apiCall('/api/endpoint', { method: 'GET', data: ... })
        url = methodOrUrl;
        config = urlOrOptions || {};
        method = config.method || 'GET';
      }
      
      const { method: _, ...restConfig } = config;
      const response = await api({
        method,
        url,
        ...restConfig
      });
      return response.data;
    } catch (error) {
      // Check if error response exists
      if (error.response?.data) {
        const errorMessage = error.response.data.error || 
                           error.response.data.message || 
                           'An error occurred';
        throw new Error(errorMessage);
      }
      
      // Network or other errors
      throw new Error(error.message || 'Network error occurred');
    }
  }, []);

  const value = useMemo(() => ({
    apiCall,
    // Direct API method access
    get: (url, config) => apiCall(url, { ...config, method: 'GET' }),
    post: (url, data, config) => apiCall(url, { ...config, method: 'POST', data }),
    put: (url, data, config) => apiCall(url, { ...config, method: 'PUT', data }),
    delete: (url, config) => apiCall(url, { ...config, method: 'DELETE' }),
    patch: (url, data, config) => apiCall(url, { ...config, method: 'PATCH', data }),
  }), [apiCall]);

  return (
    <ApiContext.Provider value={value}>
      {children}
    </ApiContext.Provider>
  );
};

export default ApiContext; 